import axios from "axios";
import { WarningRounded } from "@material-ui/icons";
import { debounce } from "lodash";
import React, { useCallback, useEffect, useState } from "react";
import BlockUi from "react-block-ui";
import { Form } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { toast } from "react-toastify";

import {
  cepMask,
  cnpjMask,
  cpfMask,
  phoneMask
} from "../../../../_metronic/_helpers/MasksHelper";
import { customSelectStyle } from "../../../../_metronic/_partials/dropdowns/ReactSelectCustomStyle";
import { PAYMENT_PROVIDERS } from "../../../constants/ClientesDados";
import {
  COURTESY_PRODUCT_OPTIONS,
  COURTESY_DURATION_TYPES,
  COURTESY_JUSSUB_DURATION_OPTIONS
} from "../../../constants/courtesy";
import { normalizeAmount } from "../../../helpers/normalizeAmount";
import ModalConfirm from "../../../components/ModalConfirm";
import { PlansAPI } from "../../../api/payments/plans/api";

export default function Step1(props) {
  const dispatch = useDispatch();
  const states = useSelector(state => state.app.states);
  const cities = useSelector(state => state.app.cities);
  const professions = useSelector(state => state.app.professions);
  const { user } = useSelector(state => state.auth);
  const [plans, setPlans] = useState([]);
  const [filteredPlans, setFilteredPlans] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isValidToRegister, setIsValidToRegister] = useState(false);
  const [isPaidFlowWarningOpen, setIsPaidFlowWarningOpen] = useState(false);

  const loadCidades = (state, city = "") => {
    dispatch({
      type: "LOAD_CITIES",
      payload: {
        state: state,
        callback: _cities => {
          if (city !== "") {
            setTimeout(() => {
              const found_city = _cities.find(
                value_city => value_city.name === city
              );

              if (found_city) {
                props.formik.setFieldValue("city", found_city);
              }
            }, 1500);
          }
        }
      }
    });
  };
  const chooseCEP = async cep => {
    if (!cep || cep.replace(/\D/g, "").length < 8) {
      return;
    }
    var request = new XMLHttpRequest();
    request.open(
      "get",
      `https://viacep.com.br/ws/${cep.replace(/\D/g, "")}/json/`,
      true
    );
    request.send();
    request.onload = function() {
      const result = JSON.parse(this.response);
      let state = states.find(state => state.state === result.uf);
      if (state) {
        props.formik.setFieldValue("state", state);
        props.formik.setFieldValue("address", result.logradouro);
        props.formik.setFieldValue("district", result.bairro);

        loadCidades(state.state, result.localidade);
      }
    };
  };

  const debouncedFilter = useCallback(
    debounce(inputValue => {
      if (inputValue) {
        const filtered = plans.filter(plan =>
          plan.name.toLowerCase().includes(inputValue.toLowerCase())
        );
        setFilteredPlans(filtered);
      } else {
        setFilteredPlans(plans.slice(0, 10));
      }
    }, 300),
    [plans]
  );

  useEffect(() => {
    let cancelled = false;

    const getPlans = async () => {
      try {
        setIsLoading(true);

        if (props.useJusSub) {
          const response = await PlansAPI.list();
          const plansList = response.data?.content || [];
          if (!cancelled) {
            setPlans(plansList);
            setFilteredPlans(plansList.slice(0, 10));
          }
          return;
        }

        const response = await axios.get(
          process.env.REACT_APP_API_URL + "/subscription/plans_admin"
        );
        if (!cancelled && response.data?.plans) {
          const plansList = response.data.plans;
          setPlans(plansList);
          setFilteredPlans(plansList.slice(0, 10));
        }
      } catch (err) {
        // no fluxo JusSub o erro já é exibido via toast pela camada de API
        if (!cancelled && !props.useJusSub) {
          toast.error("Ocorreu um erro ao carregar os planos.");
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    getPlans();

    return () => {
      cancelled = true;
    };
  }, [props.useJusSub]);

  const MIN_VALID_PLAN_AMOUNT = 500;

  const getNormalizedPlanAmount = value => {
    let normalizedAmount = normalizeAmount(value.amount);
    if (props.useJusSub) {
      normalizedAmount = Math.round(value.amount * 100); // planos JusSub retornam valor em reais; converte para centavos uma única vez
    }
    return normalizedAmount;
  };

  const handleOnChangePlan = value => {
    const normalizedAmount = getNormalizedPlanAmount(value);

    if (normalizedAmount === 0) {
      toast.error(
        "Erro ao processar o valor do plano. Verifique se o valor está correto."
      );
      setIsValidToRegister(false);
      props.formik.setFieldError("plan", "Valor do plano inválido.");
      return;
    }

    props.formik.setFieldValue("plan", value);
    if (normalizedAmount < MIN_VALID_PLAN_AMOUNT) {
      toast.error(
        `Não foi possível realizar a operação. Valor mínimo permitido para boletos: R$${Number(
          MIN_VALID_PLAN_AMOUNT / 100
        ).toFixed(2)}`
      );
      setIsValidToRegister(false);
      props.formik.setFieldError("plan", "O valor do plano está abaixo do mínimo permitido.");
      return;
    }
    setIsValidToRegister(true);
    props.formik.setFieldError("plan", undefined);
  };

  useEffect(() => {
    if (props.formik.values.is_courtesy) {
      return;
    }
    const plan = props.formik.values.plan;
    setIsValidToRegister(
      Boolean(plan) && getNormalizedPlanAmount(plan) >= MIN_VALID_PLAN_AMOUNT
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.formik.values.is_courtesy]);

  const handleCadastrarClick = async () => {
    const errors = await props.formik.validateForm();
    if (Object.keys(errors).length > 0) {
      props.formik.setTouched(
        Object.keys(errors).reduce((acc, key) => ({ ...acc, [key]: true }), {})
      );
      toast.error(Object.values(errors)[0] || "Existem campos obrigatórios não preenchidos.");
      return;
    }

    if (!props.formik.values.is_courtesy) {
      setIsPaidFlowWarningOpen(true);
      return;
    }
    await props.formik.handleSubmit();
  };

  const canSubmit = props.formik.values.is_courtesy
    ? Boolean(props.formik.values.courtesy_product)
    : isValidToRegister;

  return (
    <BlockUi tag="div" blocking={props.formik.isSubmitting}>
      <div className="row no-gutters mb-20">
        <div className="col-xl-12">
          <div
            className="border-radius-only-left"
            style={{ backgroundColor: "#fff", padding: "40px", height: "100%" }}
          >
            <div className="row mb-5">
              <div className="col-xl-4">
                <label className="form-label">
                  Profissão <span class="text-danger">&nbsp;*</span>
                </label>
                <Select
                  name="profession"
                  isSearchable={true}
                  isClearable={true}
                  options={professions}
                  className={
                    props.formik.errors.profession &&
                    props.formik.touched.profession
                      ? "is-invalid"
                      : ""
                  }
                  onChange={value => {
                    props.formik.setFieldValue("profession", value);
                  }}
                  value={props.formik.values.profession}
                  placeholder="Selecione.."
                  getOptionLabel={option => option.name}
                  getOptionValue={option => option.id}
                  noOptionsMessage={() => <span>Sem resultados.</span>}
                  theme={customSelectStyle}
                />
                {props.formik.errors.profession &&
                  props.formik.touched.profession && (
                  <label className="invalid-feedback">
                    {props.formik.errors.profession}
                  </label>
                )}
              </div>
              {props.formik.values.profession != null &&
                props.formik.values.profession.name === "Advogado" && (
                <>
                  <div className="col-xl-4">
                    <label className="form-label">Nº da OAB</label>
                    <input
                      type="number"
                      name="oab"
                      className={
                        "form-control " +
                          (props.formik.errors.oab && props.formik.touched.oab
                            ? "is-invalid"
                            : "")
                      }
                      value={props.formik.values.oab}
                      onChange={props.formik.handleChange}
                    />
                    {props.formik.errors.oab && props.formik.touched.oab && (
                      <label className="invalid-feedback">
                        {props.formik.errors.oab}
                      </label>
                    )}
                  </div>
                  <div className="col-xl-4">
                    <label className="form-label">Estado</label>
                    <Select
                      name="oab_state"
                      isSearchable={true}
                      isClearable={true}
                      options={states}
                      className={
                        props.formik.errors.oab_state &&
                          props.formik.touched.oab_state
                          ? "is-invalid"
                          : ""
                      }
                      onChange={value => {
                        props.formik.setFieldValue("oab_state", value);
                      }}
                      value={props.formik.values.oab_state}
                      placeholder="Selecione.."
                      getOptionLabel={option => option.state}
                      getOptionValue={option => option.state}
                      noOptionsMessage={() => <span>Sem resultados.</span>}
                      theme={customSelectStyle}
                    />
                    {props.formik.errors.oab_state &&
                        props.formik.touched.oab_state && (
                      <label className="invalid-feedback">
                        {props.formik.errors.oab_state}
                      </label>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="row mb-5">
              <div className="col-xl-6">
                <label className="form-label">
                  Nome completo <span class="text-danger">&nbsp;*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  className={
                    "form-control " +
                    (props.formik.errors.name && props.formik.touched.name
                      ? "is-invalid"
                      : "")
                  }
                  value={props.formik.values.name}
                  onChange={props.formik.handleChange}
                />
                {props.formik.errors.name && props.formik.touched.name && (
                  <label className="invalid-feedback">
                    {props.formik.errors.name}
                  </label>
                )}
              </div>
              <div className="col-xl-3">
                <label className="form-label">
                  Tipo <span class="text-danger">&nbsp;*</span>
                </label>
                <Form.Check
                  type="radio"
                  label="Pessoa Física"
                  name="type"
                  id="type"
                  checked={props.formik.values.type === "PF"}
                  onClick={() => {
                    props.formik.setFieldValue("type", "PF");
                    props.formik.setFieldValue("document_number", "");
                  }}
                />
                <Form.Check
                  type="radio"
                  label="Pessoa Jurídica"
                  name="type"
                  id="type"
                  checked={props.formik.values.type === "PJ"}
                  onClick={() => {
                    props.formik.setFieldValue("type", "PJ");
                    props.formik.setFieldValue("document_number", "");
                  }}
                />
                {props.formik.errors.type && props.formik.touched.type && (
                  <label className="invalid-feedback d-block">
                    {props.formik.errors.type}
                  </label>
                )}
              </div>
              <div className="col-xl-3">
                <label className="form-label">
                  Segmento <span class="text-danger">&nbsp;*</span>
                </label>
                <Form.Check
                  type="radio"
                  label="B2C"
                  name="client_type"
                  id="client_type_b2c"
                  checked={props.formik.values.client_type === "b2c"}
                  onClick={() => {
                    props.formik.setFieldValue("client_type", "b2c");
                  }}
                />
                <Form.Check
                  type="radio"
                  label="B2B"
                  name="client_type"
                  id="client_type_b2b"
                  checked={props.formik.values.client_type === "b2b"}
                  onClick={() => {
                    props.formik.setFieldValue("client_type", "b2b");
                  }}
                />
                {props.formik.errors.client_type &&
                  props.formik.touched.client_type && (
                    <label className="invalid-feedback d-block">
                      {props.formik.errors.client_type}
                    </label>
                  )}
              </div>
            </div>
            <div className="row mb-5">
              <div className="col-xl-4">
                <label className="form-label">
                  CPF/CNPJ <span class="text-danger">&nbsp;*</span>
                </label>
                <input
                  type="text"
                  name="document_number"
                  className={
                    "form-control " +
                    (props.formik.errors.document_number &&
                    props.formik.touched.document_number
                      ? "is-invalid"
                      : "")
                  }
                  value={props.formik.values.document_number}
                  onChange={evt => {
                    props.formik.setFieldValue(
                      "document_number",
                      props.formik.values.type === "PJ"
                        ? cnpjMask(evt.target.value)
                        : cpfMask(evt.target.value)
                    );
                  }}
                />
                {props.formik.errors.document_number &&
                  props.formik.touched.document_number && (
                  <label className="invalid-feedback">
                    {props.formik.errors.document_number}
                  </label>
                )}
              </div>
              <div className="col-xl-4">
                <label className="form-label">
                  E-mail <span class="text-danger">&nbsp;*</span>
                </label>
                <input
                  type="text"
                  name="email"
                  className={
                    "form-control " +
                    (props.formik.errors.email && props.formik.touched.email
                      ? "is-invalid"
                      : "")
                  }
                  value={props.formik.values.email}
                  onChange={(e) => {
                    const lowerCaseValue = e.target.value.toLowerCase();
                    props.formik.setFieldValue("email", lowerCaseValue);
                  }}
                />
                {props.formik.errors.email && props.formik.touched.email && (
                  <label className="invalid-feedback">
                    {props.formik.errors.email}
                  </label>
                )}
              </div>
              <div className="col-xl-4">
                <label className="form-label">
                  Telefone <span class="text-danger">&nbsp;*</span>
                </label>
                <input
                  type="text"
                  name="phone"
                  className={
                    "form-control " +
                    (props.formik.errors.phone && props.formik.touched.phone
                      ? "is-invalid"
                      : "")
                  }
                  value={props.formik.values.phone}
                  onChange={evt =>
                    props.formik.setFieldValue(
                      "phone",
                      phoneMask(evt.target.value)
                    )
                  }
                />
                {props.formik.errors.phone && props.formik.touched.phone && (
                  <label className="invalid-feedback">
                    {props.formik.errors.phone}
                  </label>
                )}
              </div>
            </div>

            <div className="row mb-5">
              <div className="col-xl-4">
                <label className="form-label">
                  CEP <span class="text-danger">&nbsp;*</span>
                </label>
                <input
                  type="text"
                  name="postal_code"
                  className={
                    "form-control " +
                    (props.formik.errors.postal_code &&
                    props.formik.touched.postal_code
                      ? "is-invalid"
                      : "")
                  }
                  value={props.formik.values.postal_code}
                  onBlur={evt => chooseCEP(evt.target.value)}
                  onChange={evt =>
                    props.formik.setFieldValue(
                      "postal_code",
                      cepMask(evt.target.value)
                    )
                  }
                  maxLength="9"
                />
                {props.formik.errors.postal_code &&
                  props.formik.touched.postal_code && (
                  <label className="invalid-feedback">
                    {props.formik.errors.postal_code}
                  </label>
                )}
              </div>
              <div className="col-xl-4">
                <label className="form-label">
                  Estado <span class="text-danger">&nbsp;*</span>
                </label>
                <Select
                  name="state"
                  isSearchable={true}
                  isClearable={true}
                  options={states}
                  className={
                    props.formik.errors.state && props.formik.touched.state
                      ? "is-invalid"
                      : ""
                  }
                  onChange={value => {
                    if (value && value !== props.formik.values.state) {
                      props.formik.setFieldValue("city", null);
                    }
                    props.formik.setFieldValue("state", value);

                    if (value) {
                      if (value._load_city) {
                        loadCidades(value.state, value._load_city);
                      } else {
                        loadCidades(value.state);
                      }
                    } else {
                      props.formik.setFieldValue("city", null);
                      loadCidades("");
                    }
                  }}
                  value={props.formik.values.state}
                  placeholder="Selecione.."
                  getOptionLabel={option => option.name}
                  getOptionValue={option => option.state}
                  noOptionsMessage={() => <span>Sem resultados.</span>}
                  theme={customSelectStyle}
                />
              </div>
              <div className="col-xl-4">
                <label className="form-label">
                  Cidade <span class="text-danger">&nbsp;*</span>
                </label>
                <Select
                  name="city"
                  isSearchable={true}
                  isClearable={true}
                  options={cities}
                  className={
                    props.formik.errors.city && props.formik.touched.city
                      ? "is-invalid"
                      : ""
                  }
                  onChange={value => props.formik.setFieldValue("city", value)}
                  value={props.formik.values.city}
                  placeholder="Selecione.."
                  getOptionLabel={option => option.name}
                  getOptionValue={option => option.id}
                  noOptionsMessage={() => <span>Sem resultados.</span>}
                  theme={customSelectStyle}
                />
              </div>
            </div>

            <div className="row mb-5">
              <div className="col-xl-6">
                <label className="form-label">
                  Endereço <span class="text-danger">&nbsp;*</span>
                </label>
                <input
                  type="text"
                  name="address"
                  className={
                    "form-control " +
                    (props.formik.errors.address && props.formik.touched.address
                      ? "is-invalid"
                      : "")
                  }
                  value={props.formik.values.address}
                  onChange={props.formik.handleChange}
                />
                {props.formik.errors.address &&
                  props.formik.touched.address && (
                  <label className="invalid-feedback">
                    {props.formik.errors.address}
                  </label>
                )}
              </div>
              <div className="col-xl-6">
                <label className="form-label">
                  Bairro <span class="text-danger">&nbsp;*</span>
                </label>
                <input
                  type="text"
                  name="district"
                  className={
                    "form-control " +
                    (props.formik.errors.district &&
                    props.formik.touched.district
                      ? "is-invalid"
                      : "")
                  }
                  value={props.formik.values.district}
                  onChange={props.formik.handleChange}
                />
                {props.formik.errors.district &&
                  props.formik.touched.district && (
                  <label className="invalid-feedback">
                    {props.formik.errors.district}
                  </label>
                )}
              </div>
            </div>

            <div className="row mb-5">
              <div className="col-xl-6">
                <label className="form-label">
                  Número <span class="text-danger">&nbsp;*</span>
                </label>
                <input
                  type="text"
                  name="address_number"
                  className={
                    "form-control " +
                    (props.formik.errors.address_number &&
                    props.formik.touched.address_number
                      ? "is-invalid"
                      : "")
                  }
                  value={props.formik.values.address_number}
                  onChange={props.formik.handleChange}
                />
                {props.formik.errors.address_number &&
                  props.formik.touched.address_number && (
                  <label className="invalid-feedback">
                    {props.formik.errors.address_number}
                  </label>
                )}
              </div>
              <div className="col-xl-6">
                <label className="form-label">Complemento</label>
                <input
                  type="text"
                  name="address_complement"
                  className="form-control"
                  value={props.formik.values.address_complement}
                  onChange={props.formik.handleChange}
                />
                {props.formik.errors.address_complement &&
                  props.formik.touched.address_complement && (
                  <label className="invalid-feedback">
                    {props.formik.errors.address_complement}
                  </label>
                )}
              </div>
            </div>

            <div className="row mb-5">
              <div className="col-xl-12 d-flex align-items-center">
                <span className="switch switch-primary mr-1">
                  <label>
                    <input
                      type="checkbox"
                      id="is_courtesy"
                      checked={props.formik.values.is_courtesy}
                      onChange={e => {
                        props.formik.setFieldValue("is_courtesy", e.target.checked);
                      }}
                    />
                    <span></span>
                  </label>
                </span>
                <label
                  htmlFor="is_courtesy"
                  className="form-label font-weight-bold mb-0 mr-2"
                  style={{ cursor: "pointer" }}
                >
                  Cortesia - sem cobrança
                </label>
                <span
                  className="font-weight-bold text-center"
                  style={{
                    fontSize: "12px",
                    letterSpacing: "0.3px",
                    padding: "4px 10px",
                    borderRadius: "4px",
                    minWidth: "88px",
                    display: "inline-block",
                    transition: "width, color 150ms ease-in, background-color 150ms ease-in",
                    color: props.formik.values.is_courtesy ? "#01AB7D" : "#5E6278",
                    backgroundColor: props.formik.values.is_courtesy
                      ? "#E8FFF3"
                      : "#F4F4F7"
                  }}
                >
                  {props.formik.values.is_courtesy ? "ATIVADO" : "DESATIVADO"}
                </span>
              </div>
              <div className="col-xl-12">
                <small className="form-text text-muted font-italic">
                  {props.formik.values.is_courtesy
                    ? "Não será gerada uma cobrança ou boleto no provedor de pagamento."
                    : "Será gerado um boleto de cobrança no valor do plano."}
                </small>
              </div>
            </div>

            {props.formik.values.is_courtesy && (
              <div className="row mb-5">
                <div className="col-xl-6">
                  <label className="form-label">
                    Tipo de liberação <span class="text-danger">&nbsp;*</span>
                  </label>
                  <CreatableSelect
                    options={COURTESY_PRODUCT_OPTIONS}
                    placeholder="Selecione ou digite o tipo de liberação"
                    value={
                      COURTESY_PRODUCT_OPTIONS.find(
                        option => option.value === props.formik.values.courtesy_product
                      ) ||
                      (props.formik.values.courtesy_product
                        ? {
                          label: props.formik.values.courtesy_product,
                          value: props.formik.values.courtesy_product
                        }
                        : null)
                    }
                    formatCreateLabel={inputValue => `Usar "${inputValue}"`}
                    onChange={option =>
                      props.formik.setFieldValue(
                        "courtesy_product",
                        option?.value?.trim().toLowerCase() || null
                      )
                    }
                  />
                  {props.formik.errors.courtesy_product && (
                    <label className="invalid-feedback d-block">
                      {props.formik.errors.courtesy_product}
                    </label>
                  )}
                </div>
                {props.useJusSub ? (
                  <div className="col-xl-6">
                    <label className="form-label">Duração</label>
                    <select
                      className="form-control"
                      value={props.formik.values.courtesy_duration}
                      onChange={e => {
                        props.formik.setFieldValue("courtesy_duration_type", "activeDays");
                        props.formik.setFieldValue("courtesy_duration", e.target.value);
                      }}
                    >
                      {COURTESY_JUSSUB_DURATION_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <>
                    <div className="col-xl-6">
                      <label className="form-label">Duração</label>
                      <select
                        className="form-control"
                        value={props.formik.values.courtesy_duration_type}
                        onChange={e =>
                          props.formik.setFieldValue(
                            "courtesy_duration_type",
                            e.target.value
                          )
                        }
                      >
                        {COURTESY_DURATION_TYPES.map(type => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-xl-6">
                      <label className="form-label">Quantidade</label>
                      <input
                        className="form-control"
                        type="number"
                        min="1"
                        step="1"
                        value={props.formik.values.courtesy_duration}
                        onChange={e =>
                          props.formik.setFieldValue(
                            "courtesy_duration",
                            e.currentTarget.value
                          )
                        }
                        placeholder="Padrão do plano"
                      />
                    </div>
                  </>
                )}
              </div>
            )}

            {/* {props.formik.values.is_courtesy && ( */}
              <div className="row mb-5">
                <div className="col-xl-6">
                  <label className="form-label">
                    Plano <span class="text-danger">&nbsp;*</span>
                  </label>
                  <Select
                    name="plan"
                    options={filteredPlans}
                    value={props.formik.values.plan}
                    isLoading={isLoading}
                    className={
                      props.formik.errors.plan && props.formik.touched.plan
                        ? "is-invalid"
                        : ""
                    }
                    onChange={value => {
                      props.formik.setFieldValue("plan", value); 
                      if (!props.formik.values.is_courtesy) handleOnChangePlan(value)}}
                    onInputChange={debouncedFilter}
                    placeholder="Selecione.."
                    getOptionLabel={option => option.name}
                    getOptionValue={option => option.id}
                    noOptionsMessage={() => <span>Sem resultados.</span>}
                    theme={customSelectStyle}
                  />
                  {props.formik.errors.plan && props.formik.touched.plan && (
                    <label className="invalid-feedback">
                      {props.formik.errors.plan}
                    </label>
                  )}
                </div>
                <div className="col-xl-6">
                  <label className="form-label">
                    Provedor de Pagamento <span class="text-danger">&nbsp;*</span>
                  </label>
                  <input
                    type="text"
                    name="provider"
                    className="form-control"
                    disabled
                    value={
                      props.useJusSub
                        ? PAYMENT_PROVIDERS.jussub
                        : props.formik.values.provider?.label ||
                          PAYMENT_PROVIDERS[props.formik.values.provider?.id] ||
                          ""
                    }
                  />
                  {props.formik.errors.provider &&
                    props.formik.touched.provider && (
                    <label className="invalid-feedback d-block">
                      {props.formik.errors.provider}
                    </label>
                  )}
                </div>
              </div>
            {/* )} */}

            <div className="row mb-5">
              <div className="col-xl-12">
                <button
                  type="button"
                  disabled={!canSubmit}
                  onClick={handleCadastrarClick}
                  className="btn btn-primary"
                  style={{ width: "100%", height: "50px" }}
                >
                  CADASTRAR
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {isPaidFlowWarningOpen && (
        <ModalConfirm
          icon={WarningRounded}
          title="Cadastrar cliente com um plano pago?"
          message="Este fluxo cobra o cliente normalmente."
          onConfirm={async () => {
            setIsPaidFlowWarningOpen(false);
            await props.formik.handleSubmit();
          }}
          onClose={() => setIsPaidFlowWarningOpen(false)}
        >
          <p
            style={{
              fontWeight: 700,
              color: "#f42a3f",
              backgroundColor: "#FBEAEC",
              borderRadius: 4,
              padding: "10px 12px",
              marginTop: 8
            }}
          >
            Se esse cadastro for uma cortesia, cancele e ative o toggle
            &quot;Cadastro via cortesia&quot; antes de cadastrar.
          </p>
        </ModalConfirm>
      )}
    </BlockUi>
  );
}
